/* eslint-disable complexity */

import { Container } from '@design-system/container';
import { SearchBlock } from '@design-system/search-block';
import { Section } from '@design-system/section';
import { Tooltip } from '@design-system/tooltip';
import classNames from 'classnames';
import React, { FC, useEffect, useRef } from 'react';

import { BurgerCloser } from './components/burger-closer';
import { BurgerOpener } from './components/burger-opener';
import { CartIcon } from './components/cart-icon';
import { FocusTrapWrapper } from './components/focus-trap-wrapper';
import { Logo } from './components/logo';
import { Navigation } from './components/navigation';
import { Profile } from './components/profile';
import { SkipLink } from './components/skip-link';
import { UtilityLinks } from './components/utility-links';
import { cartIconClassName, profileClassName } from './constants/constants';
import { useBrowserScreen } from './hooks/useBrowserScreen';
import { useBurgerMenu } from './hooks/useBurgerMenu';
import { useJS } from './hooks/useJS';
import { useStickyObserver } from './hooks/useStickyObserver';
import { ExtendedGlobalHeaderProps } from './models';
import { shouldBeRendered } from './utils/shouldBeRendered';

export const ExtendedGlobalHeader: FC<ExtendedGlobalHeaderProps> = ({
  className,
  logo,
  headerSubtitle,
  isHeaderSticky = true,
  skipLink,
  utilityLinksData,
  utilityLinksName,
  mode,
  linkNavElement,
  onClickNavItem,
  burgerToggleLabel,
  burgerOpenLabel,
  burgerCloseLabel,
  burgerOpenIcon,
  burgerCloseIcon,
  navigationName,
  navigationData,
  activeUrlPath,
  moreBtn,
  openIcon,
  closeIcon,
  languageSwitch,
  search,
  cartIcon,
  profile,
  renderOnStart,
  renderOnEnd,
  renderOnBurgerPopupStart,
  renderOnBurgerPopupEnd,
  uid,
  maxVisibleNavItems = 0,
}) => {
  const headerRef = useRef(null);
  const [isSticky] = useStickyObserver(headerRef);
  const [isJS] = useJS();
  const {
    isOpen,
    burgerBtnRef,
    handleBurgerClick,
    burgerOffsetTop,
    handleMobileNavigationItemClick,
    handleMobileLogoClick,
  } = useBurgerMenu(headerRef);
  const { isDesktopView } = useBrowserScreen();
  useEffect(() => {
    const htmlObject = document.getElementsByTagName('html')[0];
    if (isHeaderSticky) {
      htmlObject.classList.add('has-sticky-nav');
    } else {
      htmlObject.classList.remove('has-sticky-nav');
    }
  }, [isHeaderSticky]);

  const shouldRenderBurgerPopup =
    (isDesktopView && isJS) || (!isDesktopView && isOpen && isJS);

  return (
    <Section
      ref={headerRef}
      element="header"
      id="open-menu"
      mode={mode}
      removeDuplicateSpacing={false}
      data-status={isJS ? 'js' : 'no-js'}
      className={classNames('cds-global-header', className, {
        [`cds-global-header--is-sticky`]: isSticky && isHeaderSticky,
        [`cds-global-header--sticky-mode`]: isHeaderSticky,
        [`cds-global-header--burger-opened`]: !isDesktopView && isOpen,
      })}
      data-component-uid={uid}
    >
      <SkipLink {...skipLink} />
      <Container maxWidth="max-grid">
        <FocusTrapWrapper
          isOpen={isOpen}
          isMobileView={!isDesktopView}
          isJS={isJS}
        >
          <div className="header-portal">
            {renderOnStart}

            <div className="cds-global-header__mobile-main">
              {logo && (
                <Logo
                  className="cds-global-header__mobile-logo"
                  image={logo.imageSmall}
                  link={logo.link}
                  onClick={handleMobileLogoClick}
                />
              )}
              {!isDesktopView && (
                <ul className="cds-global-header__navigation-wrapper">
                  {search ? (
                    <li
                      className={classNames(
                        'cds-global-header__navigation-search',
                        'cds-global-header__navigation-icon'
                      )}
                    >
                      <SearchBlock
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...search}
                        handleBurgerMenu={handleMobileNavigationItemClick}
                      />
                    </li>
                  ) : null}
                  {profile ? (
                    <li
                      className={classNames(profileClassName, {
                        'cds-global-header__navigation-icon': !isDesktopView,
                      })}
                    >
                      <Tooltip
                        tooltipWindowClassName="cds-global-header-profile-window"
                        portalQuerySelector=".cds-tooltip"
                        label={profile.headline}
                        isTooltipContentInteractive
                        icon="profile"
                        content={<Profile {...profile} />}
                      />
                    </li>
                  ) : null}
                  {cartIcon ? (
                    <li
                      className={classNames(
                        cartIconClassName,
                        'cds-global-header__navigation-icon'
                      )}
                    >
                      <CartIcon {...cartIcon} />
                    </li>
                  ) : null}
                  <li>
                    <BurgerOpener
                      burgerToggleLabel={burgerToggleLabel}
                      burgerOpenIcon={burgerOpenIcon}
                      burgerCloseIcon={burgerCloseIcon}
                      burgerOpenLabel={burgerOpenLabel}
                      isJS={isJS}
                      handleBurgerClick={handleBurgerClick}
                      isOpen={isOpen}
                      burgerBtnRef={burgerBtnRef}
                      isMobileView={!isDesktopView}
                    />
                  </li>
                </ul>
              )}
            </div>
            {shouldRenderBurgerPopup ? (
              <div
                style={{ top: `${burgerOffsetTop}px` }}
                className="cds-global-header__burger-popup"
              >
                {renderOnBurgerPopupStart}
                <BurgerCloser
                  burgerCloseIcon={burgerCloseIcon}
                  burgerCloseLabel={burgerCloseLabel}
                  isJS={isJS}
                />
                {shouldBeRendered(
                  utilityLinksData,
                  [!isJS, isDesktopView],
                  'or'
                ) && (
                  <div className="cds-global-header__top">
                    <UtilityLinks
                      utilityLinksData={utilityLinksData}
                      linkNavElement={linkNavElement}
                      utilityLinksName={utilityLinksName}
                    />
                  </div>
                )}
                <div
                  className={classNames('cds-global-header__desktop-main', {
                    'cds-global-header__desktop-main--isMobile': !isDesktopView,
                  })}
                >
                  <Navigation
                    headerSubtitle={headerSubtitle}
                    navigationName={navigationName}
                    navigationData={navigationData}
                    activeUrlPath={activeUrlPath ?? ''}
                    linkNavElement={linkNavElement}
                    handleMobileNavigationItemClick={
                      handleMobileNavigationItemClick
                    }
                    logo={logo}
                    moreBtn={moreBtn}
                    openIcon={openIcon}
                    closeIcon={closeIcon}
                    languageSwitch={languageSwitch}
                    search={search}
                    cartIcon={cartIcon}
                    onClickNavItem={onClickNavItem}
                    profile={profile}
                    maxVisibleNavItems={maxVisibleNavItems}
                  />
                </div>
                {shouldBeRendered(
                  utilityLinksData,
                  [isJS, !isDesktopView],
                  'and'
                ) && (
                  <div className="cds-global-header__bottom">
                    <UtilityLinks
                      utilityLinksData={utilityLinksData}
                      linkNavElement={linkNavElement}
                      utilityLinksName={utilityLinksName}
                    />
                  </div>
                )}
                {renderOnBurgerPopupEnd}
              </div>
            ) : null}
            {renderOnEnd}
          </div>
        </FocusTrapWrapper>
      </Container>
    </Section>
  );
};
